import React from 'react';
import { classNames } from '../../utils/classNames';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  border?: boolean;
  variant?: 'default' | 'glow';
  accent?: 'blue' | 'yellow' | 'purple' | 'green' | 'indigo' | 'emerald' | 'orange' | 'cyan';
  accentSides?: { left?: boolean; right?: boolean }; // currently we only support side outlines
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  padding = 'md',
  border = true,
  variant = 'default',
  accent,
  accentSides,
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  if (accent) {
    // global toggle: <html data-accents="off"> disables outlines
    const accentsDisabled = typeof document !== 'undefined' && document.documentElement.getAttribute('data-accents') === 'off';
    if (accentsDisabled) {
      accent = undefined as any; // fall through to default rendering
    }
  }

  if (accent) {
    const accentBorderMap: Record<NonNullable<CardProps['accent']>, string> = {
      blue: 'border-blue-400/40',
      yellow: 'border-yellow-400/40',
      purple: 'border-purple-400/40',
      green: 'border-green-400/40',
      indigo: 'border-indigo-400/40',
      emerald: 'border-emerald-400/40',
      orange: 'border-orange-400/40',
      cyan: 'border-cyan-400/40',
    };
    const borderColor = accentBorderMap[accent] || accentBorderMap.blue;
    const sides = accentSides ?? { left: true, right: true }; // default only side outlines

    return (
      <div
        className={classNames(
          'theme-bg-card-accent backdrop-blur-sm rounded-2xl shadow-xl relative',
          'hover:shadow-2xl transition-all duration-200',
          paddingClasses[padding],
          className
        )}
      >
        {children}

        {/* side-only thin outlines using pseudo spans; 80% height (10% inset top/bottom) */}
        {sides.left && (
          <span
            className={classNames(
              'pointer-events-none absolute left-0 border-l',
              'top-[10%] bottom-[10%]',
              borderColor
            )}
          />
        )}
        {sides.right && (
          <span
            className={classNames(
              'pointer-events-none absolute right-0 border-r',
              'top-[10%] bottom-[10%]',
              borderColor
            )}
          />
        )}
      </div>
    );
  }

  if (variant === 'glow') {
    // Animated gradient border wrapper with subtle glow
    return (
      <div className={classNames('rounded-2xl', className)}>
        <div
          className={classNames(
            'rounded-2xl p-[2px] relative',
            'bg-[linear-gradient(120deg,rgba(11,31,53,0.6),rgba(44,63,80,0.7),rgba(230,206,141,0.8),rgba(214,166,80,0.7),rgba(139,150,150,0.6),rgba(11,31,53,0.6))]',
            'animate-glow-flow'
          )}
          style={{ backgroundSize: '200% 200%' }}
        >
          <div
            className={classNames(
              'theme-bg-card-glow backdrop-blur-sm rounded-2xl shadow-xl',
              'border border-slate-700/50 hover:shadow-2xl transition-all duration-300',
              paddingClasses[padding]
            )}
          >
            {children}
          </div>
          {/* soft outer glow */}
          <div className="pointer-events-none absolute inset-0 rounded-2xl blur-md opacity-30 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(230,206,141,0.4),transparent_35%),radial-gradient(circle_at_70%_80%,rgba(214,166,80,0.35),transparent_35%)]" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'theme-bg-card-default backdrop-blur-sm rounded-2xl shadow-xl border border-slate-700/50',
        'hover:shadow-2xl hover:border-slate-600/50 transition-all duration-200',
        border && 'border border-slate-700/50',
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </div>
  );
};